import { connect } from 'react-redux'
import { get, has } from 'lodash'
import { getRedirectURLToEquivalentPage } from '@scoir/redirects'
import { ErrorBoundary } from '@scoir/error-reporter'

import window from 'common/window'
import ExternalRedirect from 'components/ExternalRedirect'
import TrackRememberedUrl from 'components/TrackRememberedUrl'
import { getUnauthedFallbackUrl } from 'common/api'
import { fetchCurrentAppCycle } from 'store/application-cycle'
import { actions as featuresToggleActions } from 'store/feature-toggle'
import { actions as collegeActions } from 'store/college'

import { actions as userActions } from 'store/user'
import { useInitialize } from './use-initialize'
import { IUserStartupProps } from './types'

/**
 * This function will return the URL to redirect the user to.
 * If there is an equivalent page in another app, it will return that.
 * If there is not, it will return the unauthed fallback url (sign in).
 * @returns string
 */
const getRedirectURL = () => {
  const equivalentPageURL = getRedirectURLToEquivalentPage()
  return equivalentPageURL || `${window.location.origin}${getUnauthedFallbackUrl()}`
}

/**
 * This component loads the necessary data for the user to be able to use the app.
 * If it is not able to load the data, it will redirect the user to the unauthed fallback url.
 * If it can though, it will redirect the user to the equivalent page in another app where they're signed in.
 * @param props {IUserStartupProps}
 * @returns ReactElement
 */
const UserStartup = (props: IUserStartupProps) => {
  const { isInitialized, isNotAuthed } = useInitialize(props)

  return isInitialized ? (
    <ErrorBoundary>{props.children}</ErrorBoundary>
  ) : isNotAuthed ? (
    <>
      <ExternalRedirect to={getRedirectURL()} />
      <TrackRememberedUrl />
    </>
  ) : null
}

export default connect(
  (store: any) => {
    const user = store.user.record
    return {
      userId: get(user, 'Id'),
      userScid: get(user, 'Scid'),
      hasCollege: has(store.college, 'record'),
    }
  },
  {
    fetchFeatureToggles: featuresToggleActions.fetchFeatureToggles,
    fetchUser: userActions.fetchUser,
    fetchCollege: collegeActions.fetchCollege,
    fetchCurrentAppCycle,
  },
)(UserStartup as React.FunctionComponent)
